import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { columnLabel } from "../config/databaseFilters";
import { api, formatError, API, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import {
  X, ExternalLink, Loader2, Download, FileText, Sparkles, Table as TableIcon,
  CalendarDays, Folder, FolderOpen, Copy,
} from "lucide-react";

/**
 * ProjectPreviewModal — pop-up preview for anything living in the Projects
 * section. Given a `{item, dbNames}` combo, picks the right renderer based
 * on `item.type` / `_kind`:
 *   - `type === "ai_studio"`    → images grid + design brief
 *   - `type === "worksheet"`    → embedded file preview (Office Online / PDF)
 *   - `_kind === "list"`        → saved-list rows table
 *   - anything else (plain)     → metadata card
 *
 * Every renderer also surfaces an "Open full page" link so founders who
 * want a bigger canvas can navigate away.
 */
const AI_STUDIO_ROUTES = {
  "packaging":    "/ai/packaging-designer",
  "logo":         "/ai/logo-designer",
  "meta-ads":     "/ai/meta-ads-manager",
  "reddit-ads":   "/ai/reddit-ads-manager",
  "ads-designer": "/ai/ads-designer",
  "formulation":  "/ai/formulation-creator",
};

export default function ProjectPreviewModal({ item, dbNames = {}, onClose }) {
  if (!item) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="project-preview-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        <PreviewHeader item={item} onClose={onClose} />
        <div className="flex-1 overflow-y-auto p-6">
          <PreviewBody item={item} dbNames={dbNames} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
function PreviewHeader({ item, onClose }) {
  const kind = kindLabel(item);
  return (
    <div className="flex items-start justify-between gap-4 p-5 border-b border-shell-border bg-muted/40 shrink-0">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest font-bold text-primary">
          {kind}
        </p>
        <p className="font-display text-2xl mt-0.5 truncate" title={item.name}>
          {item.name}
        </p>
        <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
          <CalendarDays className="w-3 h-3" />
          Created {new Date(item.created_at).toLocaleString()}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        data-testid="project-preview-close"
        className="w-8 h-8 grid place-items-center rounded-full hover:bg-muted shrink-0"
        aria-label="Close preview"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function kindLabel(item) {
  if (item._kind === "list") return "Saved list";
  if (item.type === "worksheet") return "Worksheet";
  if (item.type === "ai_studio") return "AI Studio · " + (item.ai_studio_tool || "").replace(/-/g, " ");
  if (item.type === "brand_profile") return "AI Brand Profile";
  if (item.type === "financial_projection") {
    const base = (item.financial_kind === "sales-estimator" || !item.financial_kind)
      ? "Financial Projection"
      : (item.financial_kind === "value-chain" ? "Value Chain Calculator"
         : item.financial_kind === "retail-margin" ? "Retail Margin Calculator"
         : item.financial_kind === "cap-table" ? "Cap Table Modeler"
         : "Financial Projection");
    return base + " · " + ((item.financial_variant || "draft") === "result" ? "Result" : "Draft");
  }
  return "Project";
}

// ---------------------------------------------------------------------------
// Body dispatcher
// ---------------------------------------------------------------------------
function PreviewBody({ item, dbNames, onClose, fullPage = false }) {
  if (item._kind === "list") {
    return <SavedListPreview item={item} dbNames={dbNames} onClose={onClose} fullPage={fullPage} />;
  }
  if (item.type === "worksheet") {
    return <WorksheetPreview item={item} onClose={onClose} />;
  }
  if (item.type === "ai_studio") {
    return <AiStudioPreview item={item} onClose={onClose} />;
  }
  if (item.type === "financial_projection") {
    return <FinancialProjectionPreview item={item} onClose={onClose} />;
  }
  if (item.type === "brand_profile") {
    return <BrandProfilePreview item={item} onClose={onClose} />;
  }
  return <PlainProjectPreview item={item} />;
}

// ---------------------------------------------------------------------------
// AI Studio preview — images + design brief
// ---------------------------------------------------------------------------
export function AiStudioPreview({ item, onClose, showStudioLink = true }) {
  const [gen, setGen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/ai-studio/generations/${item.ai_studio_generation_id}`)
      .then(r => setGen(r.data))
      .catch(() => setGen(null))
      .finally(() => setLoading(false));
  }, [item.ai_studio_generation_id]);

  if (loading) {
    return (
      <div className="py-16 text-center text-muted-foreground text-sm inline-flex items-center gap-2 justify-center w-full">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading generation…
      </div>
    );
  }
  if (!gen) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        This generation is unavailable — it may have been deleted.
      </div>
    );
  }

  const briefEntries = Object.entries(gen.brief || {})
    .filter(([k]) => k !== "image_prompt" && k !== "raw");

  const openFullPage = AI_STUDIO_ROUTES[gen.tool] || "/ai-center";

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground">
          {gen.image_count} image{gen.image_count === 1 ? "" : "s"} · quality {gen.quality}
          {gen.used_references ? ` · ${gen.used_references} reference${gen.used_references === 1 ? "" : "s"} used` : ""}
        </p>
        <div className="flex items-center gap-2">
          {gen.prompt && (
            <button
              type="button"
              data-testid="project-preview-copy-prompt"
              onClick={() => {
                navigator.clipboard.writeText(gen.prompt);
                toast.success("Prompt copied");
              }}
              className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted inline-flex items-center gap-1"
            >
              <Copy className="w-3 h-3" /> Copy prompt
            </button>
          )}
          {showStudioLink && <Link
            to={openFullPage}
            onClick={onClose}
            data-testid="project-preview-open-full"
            className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open in AI Studio
          </Link>}
        </div>
      </div>

      {gen.image_urls && gen.image_urls.length > 0 && (
        <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="project-preview-images">
          {gen.image_urls.map((url, idx) => (
            <a
              key={idx}
              href={`${BACKEND_URL}${url}`}
              target="_blank"
              rel="noreferrer"
              className="relative block rounded-xl overflow-hidden border border-shell-border bg-muted group"
              data-testid={`project-preview-image-${idx}`}
            >
              <img
                src={`${BACKEND_URL}${url}`}
                alt={`Concept ${idx + 1}`}
                className="block w-full h-auto"
              />
              <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition">
                <span className="text-white text-xs font-semibold uppercase tracking-widest">
                  Concept {idx + 1}
                </span>
                <span className="text-white text-xs font-semibold inline-flex items-center gap-1 bg-white/10 rounded-full px-3 py-1.5 backdrop-blur">
                  <Download className="w-3 h-3" /> PNG
                </span>
              </div>
            </a>
          ))}
        </div>
      )}

      {briefEntries.length > 0 && (
        <div className="mt-6 rounded-xl border border-shell-border bg-muted/60 p-5">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            AI-generated brief
          </p>
          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            {briefEntries.map(([k, v]) => (
              <BriefRow key={k} k={k} v={v} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BriefRow({ k, v }) {
  const label = k.replace(/_/g, " ");
  const isArray = Array.isArray(v);
  const isObject = v && typeof v === "object" && !isArray;
  return (
    <div className="text-sm min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
      {isArray && v.every(x => x && typeof x === "object") ? (
        <ul className="mt-1 space-y-2">
          {v.map((row, i) => (
            <li key={i} className="rounded-lg border border-shell-border bg-white/80 p-3 text-xs space-y-1">
              {Object.entries(row).map(([rk, rv]) => (
                <div key={rk} className="flex flex-wrap gap-1.5">
                  <span className="font-semibold uppercase tracking-widest text-[10px] text-muted-foreground shrink-0">
                    {rk.replace(/_/g, " ")}:
                  </span>
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">{Array.isArray(rv) ? rv.join(", ") : String(rv)}</span>
                </div>
              ))}
            </li>
          ))}
        </ul>
      ) : isArray ? (
        <ul className="mt-1 flex flex-wrap gap-1.5">
          {v.map((item, i) => (
            <li key={i} className="rounded-full bg-white px-2.5 py-1 text-xs border border-shell-border">
              {String(item)}
            </li>
          ))}
        </ul>
      ) : isObject ? (
        <div className="mt-1 rounded-lg border border-shell-border bg-white/80 p-3 text-xs space-y-1.5">
          {Object.entries(v).map(([sk, sv]) => (
            <div key={sk} className="flex flex-wrap gap-1.5">
              <span className="font-semibold uppercase tracking-widest text-[10px] text-muted-foreground shrink-0">
                {sk.replace(/_/g, " ")}:
              </span>
              <span className="min-w-0 break-words [overflow-wrap:anywhere]">{Array.isArray(sv) ? sv.join(", ") : String(sv)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-1 break-words [overflow-wrap:anywhere] whitespace-pre-line">{String(v)}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Worksheet preview — inline iframe
// ---------------------------------------------------------------------------
function WorksheetPreview({ item, onClose }) {
  const [viewUrl, setViewUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!item.worksheet_id) return;
    api.post(`/worksheets/${item.worksheet_id}/view-url`)
      .then(r => setViewUrl(r.data?.url || r.data?.view_url))
      .catch(() => setError(true));
  }, [item.worksheet_id]);

  const fileHref = `${API}/worksheets/${item.worksheet_id}/file`;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" />
          {item.worksheet_title || "Worksheet"}
          {item.module_slug ? ` · Module ${item.module_slug}` : ""}
        </p>
        <div className="flex items-center gap-2">
          <a
            href={fileHref}
            target="_blank"
            rel="noreferrer"
            data-testid="project-preview-worksheet-download"
            className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted inline-flex items-center gap-1"
          >
            <Download className="w-3 h-3" /> Download
          </a>
          <a
            href={fileHref}
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
            data-testid="project-preview-worksheet-open-full"
            className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open full page
          </a>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-shell-border overflow-hidden bg-muted min-h-[420px]">
        {error ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Preview unavailable — try opening it in a new tab.
          </div>
        ) : !viewUrl ? (
          <div className="p-10 text-center text-sm text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading worksheet…
          </div>
        ) : (
          <iframe
            src={viewUrl}
            title={item.worksheet_title || "Worksheet preview"}
            data-testid="project-preview-worksheet-iframe"
            className="block w-full h-[65vh] border-0"
          />
        )}
      </div>

      {item.comments && (
        <p className="mt-4 text-sm text-muted-foreground">{item.comments}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved-list preview — resolved rows table
// ---------------------------------------------------------------------------
function SavedListPreview({ item, dbNames, onClose, fullPage = false }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const dbName = dbNames[item.database_slug] || item.database_slug;

  useEffect(() => {
    setLoading(true);
    api.get(`/lists/${item.id}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [item.id]);

  const rows = detail?.rows || [];
  // Show EVERY column (not just five) when we're in full-page mode so
  // founders don't lose data columns in the preview. Compact popup
  // keeps its old 5-column cap so it doesn't overflow horizontally.
  const columns = rows.length
    ? Array.from(new Set(rows.flatMap(r => Object.keys(r).filter(k => k !== "id"))))
        .slice(0, fullPage ? 200 : 5)
    : [];

  // ── "Open in database" link — targets the actual RECORD view (Data
  // tab) instead of the database overview. If the saved list contains
  // exactly one row, deep-link straight to that row via `?highlight`;
  // otherwise fall back to `?tab=data` so the records list opens. Both
  // patterns are already supported by DatabaseDetail's deep-link
  // handling.
  const rowIds = item.row_ids || [];
  const openHref = (() => {
    const base = `/databases/${item.database_slug}`;
    if (rowIds.length === 1) return `${base}?highlight=${encodeURIComponent(rowIds[0])}`;
    return `${base}?tab=data`;
  })();

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <TableIcon className="w-3.5 h-3.5" />
          {dbName} · {rowIds.length} record{rowIds.length === 1 ? "" : "s"}
          {rows.length !== rowIds.length && !loading && (
            <span className="text-pink-700 bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5 font-semibold">
              {rows.length} shown — {rowIds.length - rows.length} record{rowIds.length - rows.length === 1 ? " has" : "s have"} been removed from the database
            </span>
          )}
        </p>
        <Link
          to={openHref}
          onClick={onClose}
          data-testid="project-preview-list-open-full"
          className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted inline-flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" /> Open in database
        </Link>
      </div>

      {loading ? (
        <div className="mt-6 py-8 text-center text-muted-foreground text-sm inline-flex items-center gap-2 justify-center w-full">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading rows…
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground text-center py-8">
          This saved list is empty.
        </p>
      ) : (
        <div className="mt-4 overflow-hidden rounded-xl border border-shell-border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="project-preview-list-table">
              <thead className="bg-muted/60 text-xs font-semibold text-muted-foreground">
                <tr>
                  {columns.map(c => (
                    <th key={c} className="text-left px-3 py-2 font-semibold whitespace-nowrap">
                      {columnLabel(c)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-shell-border align-top">
                    {columns.map(c => (
                      <td
                        key={c}
                        className={
                          fullPage
                            ? "px-3 py-2 align-top whitespace-pre-wrap break-words max-w-[420px]"
                            : "px-3 py-2 align-top"
                        }
                      >
                        {fullPage
                          ? renderCell(r[c])
                          : truncate(String(r[c] ?? ""))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {item.comments && (
        <p className="mt-4 text-sm text-muted-foreground">{item.comments}</p>
      )}
    </div>
  );
}

function renderCell(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function truncate(s, n = 60) {
  if (!s) return "—";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

// ---------------------------------------------------------------------------
// Financial Projection preview — deep-link to the tool
// ---------------------------------------------------------------------------
function FinancialProjectionPreview({ item, onClose }) {
  const isResult = (item.financial_variant || "draft") === "result";
  const kind = item.financial_kind || "sales-estimator";
  return (
    <div>
      <div className="rounded-xl border border-shell-border bg-muted/40 p-5 grid gap-3 text-sm">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-28 shrink-0">
            Type
          </span>
          <span>Financial Projection · {isResult ? "Saved Result (view-only)" : "Editable Draft"}</span>
        </div>
        {item.financial_saved_at && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-28 shrink-0">
              Last saved
            </span>
            <span>{new Date(item.financial_saved_at).toLocaleString()}</span>
          </div>
        )}
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        {isResult
          ? "This projection was saved as a final result. Opening it will load the model in view-only mode — the same read-only behavior as a submitted Launch Blueprint."
          : "This projection was saved as a draft. Opening it will load every step with your saved values prefilled so you can continue where you left off."}
      </p>
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <Link
          to={`/tools/${kind}?snapshot=${item.id}`}
          onClick={onClose}
          data-testid={`project-preview-open-financial-${item.id}`}
          className="btn-primary text-xs inline-flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" /> Open in Financial Tool
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Brand Profile preview — deep-link to the editor in preview mode
// ---------------------------------------------------------------------------
function BrandProfilePreview({ item, onClose }) {
  const snap = item.brand_profile_snapshot || {};
  return (
    <div>
      <div className="rounded-xl border border-shell-border bg-muted/40 p-5 grid gap-3 text-sm">
        {snap.brandName && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-28 shrink-0">
              Brand
            </span>
            <span className="font-semibold">{snap.brandName}</span>
          </div>
        )}
        {snap.category && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-28 shrink-0">
              Category
            </span>
            <span>{snap.category}</span>
          </div>
        )}
        {item.brand_profile_saved_at && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-28 shrink-0">
              Saved
            </span>
            <span>{new Date(item.brand_profile_saved_at).toLocaleString()}</span>
          </div>
        )}
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        Opening this snapshot loads it into the Brand Profile editor as a preview.
        Your live brand profile is not touched until you click <strong>Replace and Save</strong>.
      </p>
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <Link
          to={`/ai-cpg-tools/brand-profile?snapshot=${item.id}`}
          onClick={onClose}
          data-testid={`project-preview-open-brand-profile-${item.id}`}
          className="btn-primary text-xs inline-flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" /> Open Snapshot
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plain project preview — metadata only
// ---------------------------------------------------------------------------
function PlainProjectPreview({ item }) {
  return (
    <div>
      <div className="rounded-xl border border-shell-border bg-muted/40 p-5 grid gap-2 text-sm">
        {item.event_date && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-28 shrink-0">
              Event date
            </span>
            <span>{new Date(item.event_date).toLocaleDateString()}</span>
          </div>
        )}
        {item.comments && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-28 shrink-0">
              Notes
            </span>
            <span>{item.comments}</span>
          </div>
        )}
        {item.parent_id && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-28 shrink-0">
              In folder
            </span>
            <span className="inline-flex items-center gap-1"><Folder className="w-3 h-3" /> {item.parent_id}</span>
          </div>
        )}
        {!item.event_date && !item.comments && !item.parent_id && (
          <p className="text-muted-foreground text-xs">
            No metadata yet — add worksheets, saved lists, or AI Studio concepts to fill this project.
          </p>
        )}
      </div>
      <p className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-1">
        <FolderOpen className="w-3 h-3" /> Plain project — used as a container for related items.
      </p>
    </div>
  );
}
